import React, { useState, useEffect, useMemo } from 'react';
import { format, isWeekend } from 'date-fns';
import { Home, Building2, Plane, Thermometer, Calendar, X, Palmtree, Baby } from 'lucide-react';
import { isCroatianHoliday } from '../../utils/croatianHolidays';
import { API_BASE_URL } from '../../config/api';

const statusOptions = [
  { key: 'office', icon: Building2, color: '#3b82f6' },
  { key: 'home', icon: Home, color: '#10b981' },
  { key: 'vacation', icon: Palmtree, color: '#079C0C' },
  { key: 'sick', icon: Thermometer, color: '#ef4444' },
  { key: 'business_trip', icon: Plane, color: '#27BBF5' },
  { key: 'parental_leave', icon: Baby, color: '#f59e0b' },
  { key: 'other', icon: Calendar, color: '#8b5cf6' }
];

const ATTACHMENT_ENABLED_STATUSES = new Set(['sick', 'other']);

function StatusModal({
  showModal,
  setShowModal,
  selectedDay,
  selectedRange,
  updateStatus,
  clearSelection,
  currentUser,
  getStatusForUserAndDate,
  t,
  adminEditMode = false,
  currentUserRole = 'user',
  collectiveVacations = []
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [isReplacingAttachment, setIsReplacingAttachment] = useState(false);
  const apiOrigin = useMemo(() => API_BASE_URL.replace(/\/api\/v1$/, ''), []);
  const [attachmentPulse, setAttachmentPulse] = useState(false);

  // Restrict date selection to current month or future, unless an admin is
  // editing in admin-edit-mode (mirrors CalendarTable.js's isCellEditable bypass)
  const isAdminDateBypass = adminEditMode && currentUserRole === 'admin';
  const now = new Date();
  const firstDayCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const minStartDateStr = isAdminDateBypass ? null : format(firstDayCurrentMonth, 'yyyy-MM-dd');

  // Helper function to check if a date is a collective vacation day
  const isCollectiveVacationDay = (date) => {
    return collectiveVacations.some(cv => {
      const cvDate = new Date(cv.date);
      return format(cvDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
    });
  };

  useEffect(() => {
    if (showModal && selectedDay) {
      const init = format(selectedDay, 'yyyy-MM-dd');
      const clamped = minStartDateStr && init < minStartDateStr ? minStartDateStr : init;
      setStartDate(clamped);
      setEndDate(clamped);
    } else if (showModal && selectedRange.length > 0) {
      const sortedRange = [...selectedRange].sort((a, b) => a - b);
      const initStart = format(sortedRange[0], 'yyyy-MM-dd');
      const initEnd = format(sortedRange[sortedRange.length - 1], 'yyyy-MM-dd');
      const clampedStart = minStartDateStr && initStart < minStartDateStr ? minStartDateStr : initStart;
      const adjustedEnd = initEnd < clampedStart ? clampedStart : initEnd;
      setStartDate(clampedStart);
      setEndDate(adjustedEnd);
    }
  }, [showModal, selectedDay, selectedRange, minStartDateStr]);

  useEffect(() => {
    if (!showModal || !currentUser) {
      return;
    }

    let referenceDate = null;

    if (selectedDay) {
      referenceDate = selectedDay;
    } else if (selectedRange.length === 1) {
      referenceDate = selectedRange[0];
    }

    const existingStatus = referenceDate ? getStatusForUserAndDate(currentUser, referenceDate) : null;
    const statusKey = existingStatus?.status || null;

    setSelectedStatus(statusKey);
    setPendingStatus(ATTACHMENT_ENABLED_STATUSES.has(statusKey) ? statusKey : null);

    if (existingStatus && existingStatus.attachment_url) {
      const absoluteUrl = existingStatus.attachment_url.startsWith('http')
        ? existingStatus.attachment_url
        : `${apiOrigin}${existingStatus.attachment_url}`;

      setExistingAttachment({
        name: existingStatus.attachment_name || existingStatus.attachment_url.split('/').pop(),
        url: absoluteUrl,
        mimeType: existingStatus.attachment_mime_type || undefined,
      });
      setIsReplacingAttachment(false);
      setAttachedFile(null);
    } else {
      setExistingAttachment(null);
      setIsReplacingAttachment(false);
      setAttachedFile(null);
    }
  }, [showModal, currentUser, selectedDay, selectedRange, getStatusForUserAndDate, apiOrigin]);

  const handleStartChange = (e) => {
    let val = e.target.value;
    if (val && minStartDateStr && val < minStartDateStr) {
      val = minStartDateStr;
    }
    setStartDate(val);
    if (endDate && endDate < val) {
      setEndDate(val);
    }
  };

  const handleEndChange = (e) => {
    let val = e.target.value;
    const effectiveMin = startDate || minStartDateStr;
    if (val && effectiveMin && val < effectiveMin) {
      val = effectiveMin;
    }
    setEndDate(val);
  };

  const handleClose = () => {
    setShowModal(false);
    setStartDate('');
    setEndDate('');
    setPendingStatus(null);
    setSelectedStatus(null);
    setAttachedFile(null);
    setExistingAttachment(null);
    setIsReplacingAttachment(false);
    clearSelection(); // Clear blue cell selection when modal closes
  };

  const handleStatusSet = (status) => {
    let datesToUpdate = [];

    if (startDate && endDate) {
      // Use date range from inputs
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        alert(t('invalidDateRange'));
        return;
      }

      let current = new Date(start);
      while (current <= end) {
        // Only include workdays, skip weekends, holidays, and collective vacations
        if (!isWeekend(current) && !isCroatianHoliday(current) && !isCollectiveVacationDay(current)) {
          datesToUpdate.push(new Date(current));
        }
        current.setDate(current.getDate() + 1);
      }
    } else if (selectedRange.length > 0) {
      // Filter out collective vacation days from selected range
      datesToUpdate = selectedRange.filter(date => !isCollectiveVacationDay(date));
    } else if (selectedDay) {
      datesToUpdate = [selectedDay];
    }

    const options = ATTACHMENT_ENABLED_STATUSES.has(status) && attachedFile
      ? { attachment: attachedFile }
      : undefined;
    const success = updateStatus(datesToUpdate, status, currentUser, t, options);
    if (success) {
      handleClose();
      clearSelection();
    }
  };

  const handleStatusClick = (statusKey) => {
    if (statusKey !== selectedStatus) {
      setAttachedFile(null);
      setExistingAttachment(null);
      setIsReplacingAttachment(false);
    }
    setSelectedStatus(statusKey);

    if (ATTACHMENT_ENABLED_STATUSES.has(statusKey)) {
      setPendingStatus(statusKey);
    } else {
      setPendingStatus(null);
      handleStatusSet(statusKey);
    }
  };

  useEffect(() => {
    setAttachmentPulse(ATTACHMENT_ENABLED_STATUSES.has(selectedStatus));
  }, [selectedStatus]);

  const handleDelete = () => {
    let datesToUpdate = [];

    if (selectedRange.length > 0) {
      datesToUpdate = selectedRange;
    } else if (selectedDay) {
      datesToUpdate = [selectedDay];
    }

    updateStatus(datesToUpdate, null, currentUser, t);
    handleClose();
    clearSelection();
  };

  const getSelectedDaysCount = () => {
    if (selectedRange.length > 0) {
      return selectedRange.length;
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start > end) {
        return 0;
      }

      let count = 0;
      let current = new Date(start);
      while (current <= end) {
        // Only count workdays, skip weekends and holidays
        if (!isWeekend(current) && !isCroatianHoliday(current)) {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }
      return count;
    }

    return 0;
  };

  if (!showModal) return null;

  const isMultiDay = selectedRange.length > 1;
  const selectedStatusConfig = statusOptions.find(({ key }) => key === selectedStatus);
  const attachmentHighlightColor = ATTACHMENT_ENABLED_STATUSES.has(selectedStatus) ? selectedStatusConfig?.color : null;

  // Check if there are existing statuses to delete
  const hasExistingStatuses = () => {
    if (selectedDay) {
      // Single day - check if current user has status for this day
      return !!getStatusForUserAndDate(currentUser, selectedDay);
    } else if (selectedRange.length > 0) {
      // Multiple days - check if current user has status for any of these days
      return selectedRange.some(date => getStatusForUserAndDate(currentUser, date));
    }
    return false;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
            {adminEditMode && currentUserRole === 'admin' && currentUser !== null ?
              `${t('setWorkStatus')} - ${currentUser}` :
              t('setWorkStatus')}
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* Date Range Inputs */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                {t('startDate')}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={handleStartChange}
                min={minStartDateStr || undefined}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                {t('endDate')}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={handleEndChange}
                min={startDate || minStartDateStr || undefined}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {(selectedRange.length > 0 || (startDate && endDate)) && (
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
              {t('selectedDaysCount', { count: getSelectedDaysCount() })}
            </p>
          )}
        </div>

        {/* Status Options */}
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
            {t('selectStatus')}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
            {statusOptions.map(({ key, icon: Icon, color }) => {
              const isAttachmentStatus = ATTACHMENT_ENABLED_STATUSES.has(key);
              const isSelectedAttachmentStatus = isAttachmentStatus && selectedStatus === key;

              const buttonStyles = {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '16px 12px',
                backgroundColor: isSelectedAttachmentStatus ? `${color}10` : 'white',
                border: `2px solid ${isSelectedAttachmentStatus ? color : '#e5e7eb'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                color: '#374151',
                transition: 'all 0.2s'
              };

              const handleMouseLeave = (e) => {
                if (isSelectedAttachmentStatus) {
                  e.currentTarget.style.borderColor = color;
                  e.currentTarget.style.backgroundColor = `${color}10`;
                } else {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = 'white';
                }
              };

              return (
                <button
                  key={key}
                  onClick={() => handleStatusClick(key)}
                  style={buttonStyles}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = color;
                    e.currentTarget.style.backgroundColor = `${color}10`;
                  }}
                  onMouseLeave={handleMouseLeave}
                >
                  <Icon size={24} color={color} />
                  {t(`statusTypes.${key}`)}
                </button>
              );
            })}
          </div>
        </div>

        {pendingStatus && (
          <div
            className={attachmentPulse ? 'attachment-pulse' : undefined}
            style={{
              marginBottom: '20px',
              padding: '12px',
              border: `2px solid ${attachmentHighlightColor || '#e5e7eb'}`,
              borderRadius: '8px',
              backgroundColor: '#f9fafb',
              transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
              '--attachment-pulse-color': attachmentHighlightColor ? `${attachmentHighlightColor}55` : 'transparent'
            }}
          >
            <div style={{ marginBottom: '12px' }}>
              {existingAttachment && !isReplacingAttachment ? (
                <>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    {t('existingDocument')}
                  </label>
                  <a
                    href={existingAttachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
                  >
                    {t('downloadAttachment', { defaultValue: 'Download attachment' })}
                    {existingAttachment.name ? `: ${existingAttachment.name}` : ''}
                  </a>
                  <div style={{ marginTop: '12px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsReplacingAttachment(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500
                      }}
                    >
                      {t('replaceDocument')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    {t('uploadDocument')}
                  </label>
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      setAttachedFile(file);
                    }}
                    disabled={!selectedStatus || !ATTACHMENT_ENABLED_STATUSES.has(selectedStatus)}
                    style={{
                      width: '100%',
                      padding: '6px 0',
                      fontSize: '14px'
                    }}
                  />
                  {attachedFile && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                      {t('selected', { name: attachedFile.name })}
                    </p>
                  )}
                  {existingAttachment && (
                    <div style={{ marginTop: attachedFile ? '8px' : '12px' }}>
                      <a
                        href={existingAttachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {t('downloadAttachment', { defaultValue: 'Download attachment' })}
                        {existingAttachment.name ? `: ${existingAttachment.name}` : ''}
                      </a>
                    </div>
                  )}
                  {existingAttachment && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setAttachedFile(null);
                          setIsReplacingAttachment(false);
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f9fafb',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                      >
                        {t('cancelReplaceDocument')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: hasExistingStatuses() ? 'space-between' : 'flex-end', gap: '12px' }}>
          {hasExistingStatuses() && (
            <button
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {isMultiDay ? t('deleteStatuses') : t('deleteStatus')}
            </button>
          )}
          {pendingStatus && (
            <button
              onClick={() => handleStatusSet(pendingStatus)}
              style={{
                padding: '10px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {t('setStatus')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default StatusModal;
